import React, { useContext, useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Flex, Typography } from "antd";
import { AppContext } from "../../../context/AppContext";
import CommonMessageTextArea from "../../../components/main/CommonMessageTextArea";
import useHttp from "../../../hooks/useHttp";
import { mapToMediaList } from "../../../pages/utils/commonUtils";
import type { ActionNodeData } from "../nodes/ActionNode";
import HCheckBoxGroup from "../../../voice/components/common/HCheckBoxGroup";
import { useWorkflowStore } from "../store";
import {
    getTemplateMetaFromPreviewPayload,
    previewHasMergeableContent,
    type TemplatePreviewResponse,
} from "./templatePreviewTypes";

const { Text } = Typography;

const noop = (): void => undefined;

function mapPreviewMediaToPersistable(
    mediaList: unknown[],
    toPersistableMedia: (items: unknown[]) => unknown[]
): unknown[] {
    return toPersistableMedia(
        mediaList.map((f) => {
            const item = f as Record<string, unknown>;
            return {
                url: item?.url,
                name: item?.name ?? "file",
                size: item?.size ?? -1,
                contentType: (item?.contentType as string) ?? (item?.type as string) ?? "dummy/dummy",
            };
        })
    );
}

export interface AutomatedResponseFormProps {
    data: ActionNodeData;
    update: (d: Partial<ActionNodeData>) => void;
}

/** Shown in the textarea when a template is selected (full body stays in `data.message` for save/API). */
function templateSelectionDisplayText(templateName: string | undefined): string {
    const name = templateName?.trim();
    if (name) return name;
    return "Selected template";
}

/** Automated Response action form - uses CommonMessageTextArea for message input (templates, variables, emoji) */
export const AutomatedResponseForm: React.FC<AutomatedResponseFormProps> = ({ data, update }) => {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const applyOncePerContact = useWorkflowStore((s) => s.applyOncePerContact);
    const setApplyOncePerContact = useWorkflowStore((s) => s.setApplyOncePerContact);
    const [fileList, setFileList] = useState<unknown[]>([]);
    const [previewResponse, setPreviewResponse] = useState<TemplatePreviewResponse | null>(null);
    const [templateId, setTemplateId] = useState<string | undefined>(data.templateId);
    const [shortcutTemplateOpen, setShortcutTemplateOpen] = useState(false);
    const [iconTemplateOpen, setIconTemplateOpen] = useState(false);
    const [messageAreaLoading, setMessageAreaLoading] = useState(false);
    const messageTextAreaContentRef = useRef<HTMLDivElement>(null);
    /** Latest template id for this form — ignore stale preview fetches after rapid changes. */
    const latestTemplateIdRef = useRef<string | undefined>(data.templateId);
    const messageFallbackRef = useRef<string | undefined>(data.message);

    /**
     * Plain-text templates: textarea shows template name while `data.message` keeps resolved body for `messageBody`.
     * WhatsApp-style `components` preview uses ComponentPreview; textarea value is not shown.
     */
    const handleMessage = useCallback(
        (message: string, _event?: unknown, source?: string) => {
            if (source === "templates") {
                update({ message });
                return;
            }
            if (data.templateId && !data.components && !previewResponse?.components) {
                const displayLabel = templateSelectionDisplayText(data.templateName);
                if (message === displayLabel) {
                    return;
                }
                update({
                    message,
                    templateId: undefined,
                    templateName: undefined,
                    templateIntegId: undefined,
                    components: undefined,
                    fieldNameAndValueMap: undefined,
                    hasMedia: false,
                    mediaFileList: [],
                });
                setPreviewResponse(null);
                setTemplateId(undefined);
                return;
            }
            update({ message });
        },
        [data.templateId, data.templateName, data.components, previewResponse?.components, update]
    );

    const toPersistableMedia = useCallback((items: unknown[]) => {
        return items.map((f) => {
            const item = f as Record<string, unknown>;
            return {
                url: item?.url as string | undefined,
                name: (item?.name as string) ?? "file",
                size: (item?.size as number) ?? -1,
                contentType: (item?.contentType as string) ?? (item?.type as string) ?? "dummy/dummy",
                thumbnailUrl: (item?.thumbnailUrl as string) ?? (item?.url as string),
            };
        });
    }, []);

    const handlePreviewResponse = useCallback(
        (response: TemplatePreviewResponse | null) => {
            setPreviewResponse(response);
            if (response == null) {
                return;
            }

            const mediaList = Array.isArray(response.mediaFileList) ? response.mediaFileList : [];
            const persistableMedia = mapPreviewMediaToPersistable(mediaList, toPersistableMedia);

            const contentPatch: Partial<ActionNodeData> = {
                hasMedia: mediaList.length > 0,
                mediaFileList: persistableMedia,
            };
            if (typeof response.data === "string") {
                contentPatch.message = response.data;
            }
            if (response.components !== undefined) {
                contentPatch.components = response.components;
            }
            if (response.fieldNameAndValueMap !== undefined) {
                contentPatch.fieldNameAndValueMap = response.fieldNameAndValueMap;
            }

            const meta = getTemplateMetaFromPreviewPayload(response as Record<string, unknown>);
            if (meta) {
                update({
                    ...contentPatch,
                    templateId: meta.templateId,
                    templateName: meta.templateName,
                    templateIntegId: meta.templateIntegId,
                    message: typeof response.data === "string" ? response.data : contentPatch.message ?? "",
                });
                return;
            }

            if (previewHasMergeableContent(response)) {
                update(contentPatch);
            }
        },
        [update, toPersistableMedia]
    );

    const handlePreviewRemove = useCallback(() => {
        setPreviewResponse(null);
        setTemplateId(undefined);
        update({
            message: "",
            templateId: undefined,
            templateName: undefined,
            templateIntegId: undefined,
            hasMedia: false,
            mediaFileList: [],
            components: undefined,
            fieldNameAndValueMap: undefined,
        });
    }, [update]);

    const handleMediaFileList = useCallback(
        (list: unknown[] | ((prev: unknown[]) => unknown[])) => {
            if (typeof list === "function") {
                setFileList((prev) => {
                    const nextList = list(prev);
                    const arr = Array.isArray(nextList) ? nextList : [];
                    update({ hasMedia: arr.length > 0, mediaFileList: toPersistableMedia(arr) });
                    return nextList;
                });
            } else {
                const arr = Array.isArray(list) ? list : [];
                setFileList(list);
                update({ hasMedia: arr.length > 0, mediaFileList: toPersistableMedia(arr) });
            }
        },
        [update, toPersistableMedia]
    );

    useEffect(() => {
        if (data.templateId !== undefined) setTemplateId(data.templateId);
        latestTemplateIdRef.current = data.templateId;
        messageFallbackRef.current = data.message;
    }, [data.templateId, data.message]);

    useEffect(() => {
        const persisted = data.mediaFileList;
        if (Array.isArray(persisted) && persisted.length > 0) {
            setFileList(mapToMediaList(persisted, false));
        } else {
            setFileList([]);
        }
    }, [data.mediaFileList, data.templateId]);

    useEffect(() => {
        const tid = data.templateId;
        if (!tid || previewResponse?.components || !appProperties) return;
        const integId = data.templateIntegId ?? appProperties?.randomIntegId;
        if (!integId) return;

        const requestTemplateId = tid;
        let effectActive = true;

        setMessageAreaLoading(true);
        fetchData(
            `omessage/${integId}/template/${requestTemplateId}/preview?associatedObjectId=&associatedObjectType=&phone=`,
            "GET",
            null,
            appProperties
        )
            .then((response) => {
                if (!effectActive) return;
                if (latestTemplateIdRef.current !== requestTemplateId) return;
                if (typeof response !== "string") return;
                const responseData = JSON.parse(response) as { data?: Record<string, unknown> };
                if (
                    !(
                        responseData?.data?.data ||
                        responseData?.data?.mediaFileList ||
                        responseData?.data?.components
                    )
                ) {
                    return;
                }
                const preview = responseData.data as Record<string, unknown>;
                if (latestTemplateIdRef.current !== requestTemplateId) return;

                setPreviewResponse(preview as TemplatePreviewResponse);
                const mediaList = Array.isArray(preview?.mediaFileList) ? preview.mediaFileList : [];
                const persistableMedia = mapPreviewMediaToPersistable(mediaList, toPersistableMedia);
                const meta = getTemplateMetaFromPreviewPayload(preview);

                const contentPatch: Partial<ActionNodeData> = {
                    message:
                        (preview?.data as string | undefined) ?? messageFallbackRef.current ?? "",
                    hasMedia: mediaList.length > 0,
                    mediaFileList: persistableMedia,
                    components: preview?.components,
                    fieldNameAndValueMap: preview?.fieldNameAndValueMap as Record<string, unknown> | undefined,
                };

                if (meta) {
                    update({
                        ...contentPatch,
                        templateId: meta.templateId,
                        templateName: meta.templateName,
                        templateIntegId: meta.templateIntegId,
                    });
                } else if (previewHasMergeableContent(preview as TemplatePreviewResponse)) {
                    update(contentPatch);
                }
            })
            .catch(() => undefined)
            .finally(() => {
                if (effectActive) {
                    setMessageAreaLoading(false);
                }
            });

        return () => {
            effectActive = false;
        };
    }, [data.templateId, data.templateIntegId, appProperties, fetchData, previewResponse?.components, toPersistableMedia, update]);

    const messageForTextArea = useMemo(() => {
        if (data.templateId && !previewResponse?.components && !data.components) {
            return templateSelectionDisplayText(data.templateName);
        }
        return data.message ?? "";
    }, [data.templateId, data.templateName, data.message, data.components, previewResponse?.components]);

    if (!appProperties) return null;

    const commonMessageProps = {
        message: messageForTextArea,
        handleMessage,
        handleMedia: noop,
        handleMediaFileList,
        handlePreviewResponse,
        appProperties,
        fileList,
        source: "automation" as const,
        integId: appProperties?.randomIntegId,
        moduleName: "",
        fetchAllTemplates: true,
        messageTextAreaContentRef,
        templateId,
        setTemplateId,
        previewResponse,
        shortcutTemplateOpen,
        setShortcutTemplateOpen,
        iconTemplateOpen,
        setIconTemplateOpen,
        chatView: false,
        textAreaMinRows: 4,
        fetchAndShowTemplatePreview: true,
        hiddenItems: [] as string[],
        messageAreaLoading,
        setMessageAreaLoading,
        handleManageSubscription: noop,
        toggleDrawer: noop,
        handlePreviewRemove,
        isContentPresent: !!(
            data.templateId ||
            data.message ||
            fileList.length > 0 ||
            previewResponse?.components ||
            data.components
        ),
        filesPreviewPosition: "bottom",
    };

    return (
        <Flex vertical gap="middle" className="px-1 py-3">
            <Text className="trigger-rule-config-panel__label hs-fw-500 hs-fs-12 hs-color-BlackRussian">Message</Text>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <CommonMessageTextArea {...(commonMessageProps as any)} />
            <div className="hs-fs-12">
                <HCheckBoxGroup
                    options={[
                        { label: "Apply once per contact", value: "true" },
                    ]}
                    checkedValue={(applyOncePerContact ?? false) ? "true" : "false"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setApplyOncePerContact((e.target as HTMLInputElement).checked)
                    }
                    optionsFontSize={12}
                />
                <Text className="hs-fs-12 hs-color-mediumDark d-block mt-1">
                    When enabled, this workflow runs only once per contact.
                </Text>
            </div>
        </Flex>
    );
};
